import * as React from "react";
import * as yup from "yup";
import * as Comp from "../../../../components";
import { Paths } from "../../../../constants";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { Api } from "../../../../services/Api";
import { ReactHelper } from "../../../../utils";

interface OwnProps {
    api: Api;
    isAdmin: boolean;
    show: boolean;
    onCancel: () => void;
    onSave: (author: Forms.Abstract.ComposeNewCoAuthorData) => void;
}

interface OwnState {
    coAuthor: Forms.Abstract.ComposeNewCoAuthorData;
    dupeUser?: M.User.BasicProfile;
    dupeNameUser?: M.User.BasicProfile;
    errors: M.Validation.IFieldValidationError[];
}

export class CoAuthorForm extends React.Component<OwnProps, OwnState> {

    private _emailCheckTimeout: NodeJS.Timer | undefined;
    private _nameCheckTimeout: NodeJS.Timer | undefined;

    public constructor(props: any) {
        super(props);
        this.state = {
            coAuthor: {
                ...Forms.Abstract.DefaultComposeNewCoAuthor,
            },
            errors: [],
        };
    }

    public getNewAuthor() {
        return { ...this.state.coAuthor };
    }

    public render() {
        if (!this.props.show)
            return null;

        let fieldErrors: string[] = [];
        for (const e of this.state.errors)
            fieldErrors = fieldErrors.concat(e.errors);

        if (this.state.dupeNameUser) {
            const dupe = this.state.dupeNameUser;
            if (this.props.isAdmin)
                fieldErrors.push(`A user with the same name already exists <strong>
                    <a href="${Paths.Legacy.AdminMemberProfile(dupe.legacyMemberId)}" target="_blank">${dupe.username}</a></strong>`);
            else
                fieldErrors.push(`A user with the same name already exists <strong>${dupe.username}</strong>`);
        }

        const displayErrors = fieldErrors.length
            ? fieldErrors.map((e, i) => <div key={i} className="field-error" dangerouslySetInnerHTML={{ __html: e }} />)
            : null;

        return <>
            <div className="field-single-line fields-name-long">
                <p className={"field-name-first " + this.fieldErrorCss("firstName")}>
                    <input
                        className="txt"
                        placeholder="First"
                        onChange={(e) => this.updateField((a) => a.firstName = e.target.value, () => this.onNameChange())}
                        value={this.state.coAuthor.firstName || ""}
                    />
                </p>
                <p className={"field-name-last " + this.fieldErrorCss("lastName")}>
                    <input
                        className="txt"
                        placeholder="Last"
                        onChange={(e) => this.updateField((a) => a.lastName = e.target.value, () => this.onNameChange())}
                        value={this.state.coAuthor.lastName || ""}
                    />
                </p>
                <p className={"field-name-suffix " + this.fieldErrorCss("title")}>
                    <input
                        className="txt"
                        placeholder="Title(s)"
                        onChange={(e) => this.updateField((a) => a.title = e.target.value)}
                        value={this.state.coAuthor.title || ""}
                    />
                </p>
            </div>

            <p className={this.fieldErrorCss("email")}>
                <input
                    className="txt"
                    placeholder="Email address"
                    onChange={(e) => this.onEmailChange(e.target.value)}
                    value={this.state.coAuthor.email || ""}
                />
            </p>

            {displayErrors}

            <p>
                <a className="action" href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.validateAuthor(); }}>Save</a>{" "}
                <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.props.onCancel(); }}>Cancel</a>
            </p>
        </>;
    }

    private fieldErrorCss(name: string) {
        return this.state.errors
            .filter((e) => e.inputName === name && e.errors.length > 0)
            .length > 0
            ? "field-with-errors"
            : "";
    }

    private onEmailChange(value: string) {
        this.updateField((a) => a.email = value);

        if (this._emailCheckTimeout)
            clearTimeout(this._emailCheckTimeout);

        this._emailCheckTimeout = setTimeout(async () => {
            if (this.state.coAuthor.email.indexOf("@") > -1) {
                // check email against existing accounts
                const qp = { queryParams: {
                    legacyCheck: true,
                    username: this.state.coAuthor.email,
                }};
                const result = await this.props.api.get(Api.endpoints.user.basicProfile, qp);
                if (result.isSuccess && result.data) {
                    // another user with the same email was found.
                    const errors = [ ...this.state.errors ].filter((x) => x.inputName !== "email-check");
                    const msg = this.props.isAdmin
                        ? `A user with this email address already exists <strong>
                            <a href="${Paths.Legacy.AdminMemberProfile(result.data.legacyMemberId)}" target="_blank">${result.data.username}</a></strong>`
                        : `A user with this email address already exists <strong>${result.data.username}</strong>`;
                    errors.push(new M.Validation.FieldValidationError("email-check", msg));
                    this.setState({ ...this.state, dupeUser: result.data, errors });
                }
                else {
                    // clear email check errors
                    const errors = this.state.errors.filter((x) => x.inputName !== "email-check");
                    this.setState({ ...this.state, dupeUser: undefined, errors });
                }
            }
        }, 375);
    }

    private onNameChange() {
        if (this._nameCheckTimeout)
            clearTimeout(this._nameCheckTimeout);

        this._nameCheckTimeout = setTimeout(async () => {
            if (this.state.coAuthor.firstName && this.state.coAuthor.lastName) {
                // check author name against existing accounts
                const qp = { queryParams: {
                    legacyCheck: true,
                    firstName: this.state.coAuthor.firstName,
                    lastName: this.state.coAuthor.lastName,
                }};
                const result = await this.props.api.get(Api.endpoints.user.basicProfile, qp);
                if (result.isSuccess && result.data)
                    this.setState({ ...this.state, dupeNameUser: result.data });
                else
                    this.setState({ ...this.state, dupeNameUser: undefined });
            }
        }, 375);
    }

    private updateField(deligate: (a: Forms.Abstract.ComposeNewCoAuthorData) => void, callback?: () => void) {
        const coAuthor = { ...this.state.coAuthor };
        deligate(coAuthor);
        this.setState({ ...this.state, coAuthor }, callback);
    }

    private validateAuthor() {
        const coAuthor = { ...this.state.coAuthor };
        const schema = Forms.Abstract.ComposeNewCoAuthorSchema;

        if (this.state.dupeUser)
            return;

        schema.validate(coAuthor, { abortEarly: false })
            .then((model) => {
                this.props.onSave(coAuthor);
                this.setState({
                    ...this.state,
                    coAuthor: {
                        ...Forms.Abstract.DefaultComposeNewCoAuthor,
                    },
                    errors: [],
                });
            }, (err: yup.ValidationError) => {
                const errors = Comp.Validation.YupErrorsToValidationErrors(schema, err);
                this.setState({ ...this.state, errors });
            });
    }
}



// WEBPACK FOOTER //
// ./src/containers/abstracts/composer/components/CoAuthorForm.tsx